import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Divider,
  TablePagination,
} from '@mui/material';
import { Add, Delete, Edit, Visibility } from '@mui/icons-material';
import { insuranceTypeDetailSummaryAPI } from '../../services/api';
import browserGlobal from '../../utils/browserGlobal';

const defaultDetail = {
  id: 0,
  insuranceTypeId: 0,
  coverageText: '',
};

function getDialogTitle(viewMode, editMode) {
  if (viewMode) return 'Insurance Summary Details';
  if (editMode) return 'Edit Insurance Summary';
  return 'Add Insurance Summary';
}

function getSubmitButtonLabel(isSubmitting, editMode) {
  if (isSubmitting) return 'Saving...';
  if (editMode) return 'Update';
  return 'Add';
}

function mapInsuranceTypeDetails(details, insuranceTypeId) {
  if (details?.length) {
    return details.map((detail) => ({
      ...detail,
      clientKey: detail.clientKey ?? (detail.id ? `id-${detail.id}` : `coverage-${detail.coverageText ?? 'empty'}-${detail.insuranceTypeId ?? insuranceTypeId}`),
    }));
  }
  return [{ ...defaultDetail, insuranceTypeId, clientKey: 'detail-default' }];
}

const InsuranceTypeDetailSummarys = () => {
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const [viewMode, setViewMode] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [form, setForm] = useState({
    insuranceTypeId: 0,
    insuranceTypeName: '',
    insuranceTypeDetails: [{ ...defaultDetail, clientKey: 'detail-initial' }],
  });
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [totalCount, setTotalCount] = useState(0);
  const detailKeyRef = useRef(0);

  const createDetailEntry = (overrides = {}) => {
    detailKeyRef.current += 1;
    return {
      ...defaultDetail,
      ...overrides,
      clientKey: overrides.clientKey ?? `detail-${detailKeyRef.current}`,
    };
  };

  const fetchData = async () => {
    setIsLoading(true);
    try {
      const res = await insuranceTypeDetailSummaryAPI.getInsuranceTypeDetailSummarys(page, rowsPerPage);
      if (res?.data?.success) {
        const list = res.data.payload || [];
        setData(list);
        const count = res?.data?.totalRecord ?? list.length;
        setTotalCount(count);
      }
    } catch (e) {
      console.error('Error fetching insurance summaries:', e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const handleOpen = (row = null, isView = false) => {
    if (row) {
      setEditMode(!isView);
      setViewMode(isView);
      setForm({
        insuranceTypeId: row.insuranceTypeId,
        insuranceTypeName: row.insuranceTypeName,
        insuranceTypeDetails: mapInsuranceTypeDetails(row.insuranceTypeDetails, row.insuranceTypeId),
      });
    } else {
      setEditMode(false);
      setViewMode(false);
      setForm({
        insuranceTypeId: 0,
        insuranceTypeName: '',
        insuranceTypeDetails: [createDetailEntry()],
      });
    }
    setOpen(true);
  };

  const handleClose = () => {
    setViewMode(false);
    setOpen(false);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
  };

  const handleDetailChange = (e, index) => {
    const { name, value } = e.target;
    setForm(prev => {
      const list = [...prev.insuranceTypeDetails];
      list[index] = { ...list[index], [name]: value };
      return { ...prev, insuranceTypeDetails: list };
    });
  };

  const handleAddDetail = () => {
    setForm(prev => ({
      ...prev,
      insuranceTypeDetails: [...prev.insuranceTypeDetails, createDetailEntry({ insuranceTypeId: prev.insuranceTypeId })],
    }));
  };

  const handleDeleteDetail = (index) => {
    setForm(prev => {
      const list = [...prev.insuranceTypeDetails];
      list.splice(index, 1);
      return { ...prev, insuranceTypeDetails: list.length ? list : [createDetailEntry({ insuranceTypeId: prev.insuranceTypeId })] };
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (editMode) {
        await insuranceTypeDetailSummaryAPI.updateInsuranceTypeDetailSummary(form);
      } else {
        await insuranceTypeDetailSummaryAPI.createInsuranceTypeDetailSummary(form);
      }
      fetchData();
      handleClose();
    } catch (e) {
      console.error('Error saving insurance summary:', e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (insuranceTypeId) => {
    if (!browserGlobal.confirm('Are you sure you want to delete this insurance summary?')) return;
    setIsLoading(true);
    try {
      await insuranceTypeDetailSummaryAPI.deleteInsuranceTypeDetailSummary(insuranceTypeId);
      fetchData();
    } catch (e) {
      console.error('Error deleting insurance summary:', e);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" sx={{ mb: 2 }}>Insurance Type Detail Summary</Typography>
        <Box sx={{ p: 2, bgcolor: '#fff', borderRadius: 1, boxShadow: '0 2px 4px rgba(124,58,237,0.05)' }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                size="small"
                fullWidth
                placeholder="Search insurance summaries..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    '&:hover fieldset': { borderColor: '#0F3D3E' },
                    '&.Mui-focused fieldset': { borderColor: '#0F3D3E' },
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Button
                fullWidth
                variant="contained"
                startIcon={<Add />}
                onClick={() => handleOpen()}
                sx={{
                  background: 'linear-gradient(135deg,#1b5a5c 0%,#0a2829 100%)',
                  color: '#fff',
                  '&:hover': { background: 'linear-gradient(135deg,#328284 0%,#0F3D3E 100%)' },
                }}
              >
                Add Insurance Summary
              </Button>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {isLoading ? (
        <Box display="flex" justifyContent="center" my={4}><CircularProgress sx={{ color: '#0F3D3E' }} /></Box>
      ) : (
        <TableContainer component={Paper} sx={{ boxShadow: '0 2px 4px rgba(124,58,237,0.05)', borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell>Details Count</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.filter(row => {
                  if (searchQuery === '') return true;
                  const q = searchQuery.toLowerCase();
                  return (
                    (row.insuranceTypeName || '').toLowerCase().includes(q) ||
                    String(row.insuranceTypeId).includes(q) ||
                    (row.insuranceTypeDetails || []).some(d => (d.coverageText || '').toLowerCase().includes(q))
                  );
                }).map(row => (
                <TableRow key={row.insuranceTypeId} sx={{ '&:hover': { bgcolor: '#ebf4f4' } }}>
                  <TableCell>{row.insuranceTypeId}</TableCell>
                  <TableCell>{row.insuranceTypeName}</TableCell>
                  <TableCell>{row.insuranceTypeDetails ? row.insuranceTypeDetails.length : 0}</TableCell>
                  <TableCell>
                    <IconButton onClick={() => handleOpen(row, true)} sx={{ color: '#2196f3' }} title="Show"><Visibility /></IconButton>
                    <IconButton onClick={() => handleOpen(row)} sx={{ color: '#0F3D3E' }} title="Edit"><Edit /></IconButton>
                    <IconButton onClick={() => handleDelete(row.insuranceTypeId)} sx={{ color: '#ef4444' }}><Delete /></IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <TablePagination
            component="div"
            count={totalCount}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => { setRowsPerPage(Number.parseInt(e.target.value, 10)); setPage(0); }}
            rowsPerPageOptions={[5,10,25,50]}
          />
        </TableContainer>
      )}

      {/* Dialog */}
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>{getDialogTitle(viewMode, editMode)}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} mt={1}>
            {(editMode || viewMode) && (
              <Grid item xs={12} sm={6}>
                <TextField
                  label="Insurance Type ID"
                  name="insuranceTypeId"
                  type="number"
                  value={form.insuranceTypeId}
                  onChange={handleFormChange}
                  fullWidth
                  InputProps={{ readOnly: true }}
                  disabled={viewMode}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <TextField
                label="Insurance Type Name"
                name="insuranceTypeName"
                value={form.insuranceTypeName}
                onChange={handleFormChange}
                fullWidth
                disabled={viewMode}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="h6" mt={2}>Coverage Details</Typography>
              {form.insuranceTypeDetails.map((d, idx) => (
                <Box key={d.clientKey} mt={2}>
                  <Grid container spacing={2}>
                    <Grid item xs={11}>
                      <TextField
                        label="Coverage Text"
                        name="coverageText"
                        value={d.coverageText}
                        onChange={(e) => handleDetailChange(e, idx)}
                        fullWidth
                        multiline
                        rows={2}
                        disabled={viewMode}
                      />
                    </Grid>
                    <Grid item xs={1} display="flex" alignItems="center" justifyContent="center">
                      {!viewMode && (
                        <IconButton onClick={() => handleDeleteDetail(idx)} sx={{ color: 'error.main' }} size="small"><Delete /></IconButton>
                      )}
                    </Grid>
                  </Grid>
                  {idx < form.insuranceTypeDetails.length - 1 && <Divider sx={{ my: 2 }} />}
                </Box>
              ))}
              {!viewMode && (
                <Button startIcon={<Add />} onClick={handleAddDetail} size="small" sx={{ mt: 1 }}>Add Detail</Button>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Close</Button>
          {!viewMode && (
            <Button onClick={handleSubmit} variant="contained" disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={20} /> : null} sx={{ background: 'linear-gradient(135deg,#1b5a5c 0%,#0a2829 100%)', color: '#fff' }}>
              {getSubmitButtonLabel(isSubmitting, editMode)}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default InsuranceTypeDetailSummarys;
